/* One session for the whole product.
 *
 * This is the structural change the console buys: six SPAs on six origins each held their
 * own `me` query against their own door, so signing in to Capture told Fleet nothing. One
 * origin means one host-scoped `sx_session` cookie, so there is exactly one identity call —
 * the auth door's — and every workspace inherits its answer.
 *
 * A workspace may still query its own door for workspace-scoped grants (that is a 403
 * question, not a 401 one); it must not re-ask who the user is.
 */

import {
  createAuthHooks,
  expectArray,
  expectRecord,
  type Decoder,
} from "@sx/api-client";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";

import { posthog } from "../posthog";
import { clearPrincipalBrowserState } from "./browser-storage";
import { doorClient, doorPrefix } from "./doors";
import { forgetConsoleQueryCache } from "./query-cache";
import { clearLogoutRecovery, logoutRecoveryFor } from "./logout-recovery";
import type { LogoutRecoveryGuard } from "./logout-recovery";
import { authQueryKeys } from "./query-keys";

/** The one small native browser wire we intentionally keep outside Protobuf.
 * Login, logout, and session cookies are browser navigation concerns; spelling
 * their answer here is cheaper and clearer than retaining a whole OpenAPI tree. */
export interface SessionOrganization {
  readonly id: string;
  readonly slug: string;
  readonly kind: "internal" | "customer" | "vendor";
}

export interface Session {
  readonly active: true;
  readonly cache_ttl_s: number;
  readonly credential: {
    readonly id: string;
    readonly kind: "api_key" | "session";
    readonly expires_at?: string | null;
  };
  readonly principal: {
    readonly id: string;
    readonly kind: "user" | "service";
    readonly subject: string;
    readonly display_name: string;
    readonly org: SessionOrganization;
    readonly memberships: readonly {
      readonly organization: SessionOrganization;
      readonly role: "owner" | "admin" | "billing_admin" | "member";
    }[];
    readonly grants: readonly {
      readonly project: {
        readonly id: string;
        readonly slug: string;
        readonly org: SessionOrganization;
      };
      readonly scope: string;
      readonly role: string;
    }[];
  };
  readonly project_audiences: readonly {
    readonly project_id: string;
    readonly organization_slug: string;
    readonly audience: string;
  }[];
  /** The staff member working in this seat, when someone is.
   *
   * Absent for every ordinary session, which is why it is the one optional field
   * in this shape: "nobody is acting as anybody" is the normal state of the world,
   * not a missing value. It is read off the session row by the identity door, so
   * the two identities the shell shows are both the platform's answer — a console
   * that composed either of them itself could show one while the doors answered
   * to the other. */
  readonly acting?: {
    readonly principal_id: string;
    readonly subject: string;
    readonly display_name: string;
  } | null;
}

const auth = doorClient("auth");

/** The one read that parses rather than asserts.
 *
 * This answer is fetched once, above every route, and every workspace inherits it — so a
 * session missing something `SessionOut` declares required does not break one screen, it
 * breaks all of them, and it breaks them as a `TypeError` deep inside whichever component
 * touched the missing field first. `isOperator` walks `principal.grants` and the account
 * menu walks `principal.memberships`; both are non-optional in the schema and neither is
 * guarded at its call site, because the type says they cannot be absent.
 *
 * Parsing here turns "the auth door is running a revision that does not send `grants`" from
 * an unattributable render crash into a 502 the query layer already knows how to show. */
const decodeSession: Decoder<Session> = (raw) => {
  const session = expectRecord(raw, "session");
  const principal = expectRecord(session.principal, "session.principal");
  expectArray(principal.grants, "session.principal.grants");
  expectArray(principal.memberships, "session.principal.memberships");
  expectArray(session.project_audiences, "session.project_audiences");
  expectRecord(session.credential, "session.credential");
  return raw as Session;
};

/** `sessionQuery` is the same read as `useMe`, as options: the `_console` route awaits it
 * in `beforeLoad` so identity is settled before any loader runs, and every component below
 * reads the answer out of the cache. */
export const { useMe, sessionQuery } = createAuthHooks<Session>({
  queryKey: authQueryKeys.global("me"),
  me: () => auth<Session>("/auth/session", undefined, decodeSession),
});

/** The signed-in principal, as the query cache already knows it. For a route loader,
 * which runs after `_console`'s `beforeLoad` has settled the session and therefore needs
 * no second read to key its own workspace's grant. */
export function loadedSession(
  queryClient: QueryClient,
): Session | null | undefined {
  return queryClient.getQueryData(sessionQuery.queryKey);
}

/** Sign-out must be a top-level browser POST so the auth service can clear its cookie and
 * continue through Authentik's invalidation flow. A fetch would follow that redirect in the
 * background, then the session gate would immediately start OIDC again with the old provider
 * session — the exact silent-login loop this boundary exists to prevent. */
export function beginLogout() {
  const form = document.createElement("form");
  form.method = "post";
  form.action = `${doorPrefix("auth")}/api/auth/logout`;
  form.hidden = true;
  document.body.append(form);
  form.submit();
}

interface LogoutRecoveryState {
  readonly guard: LogoutRecoveryGuard;
  readonly description: string;
  readonly canSynchronize: boolean;
  readonly error?: string;
  readonly action?: "synchronize" | "download" | "discard";
}

export interface LogoutController {
  readonly isPending: boolean;
  readonly recovery?: Omit<LogoutRecoveryState, "guard">;
  readonly mutate: () => void;
  readonly cancel: () => void;
  readonly synchronize: () => Promise<void>;
  readonly download: () => void;
  readonly discard: () => void;
}

export function useLogout(principalId?: string): LogoutController {
  const queryClient = useQueryClient();
  const [isPending, setIsPending] = useState(false);
  const [recoveryState, setRecoveryState] = useState<LogoutRecoveryState>();

  const finish = useCallback(() => {
    if (principalId === undefined) return;
    queryClient.clear();
    // …and then keep nothing more. Clearing is not enough on its own: this page stays
    // alive until the browser follows the sign-out POST, and in that window every
    // mounted observer refetches, so the persister's next throttled write would put a
    // pre-logout snapshot back on disk. This settles the slot and unsubscribes.
    forgetConsoleQueryCache();
    clearPrincipalBrowserState(principalId);
    clearLogoutRecovery(principalId);
    posthog.reset();
    beginLogout();
  }, [principalId, queryClient]);

  const mutate = useCallback(() => {
    if (principalId === undefined || isPending) return;
    setIsPending(true);
    const recovery = logoutRecoveryFor(principalId);
    const description = recovery?.describe();
    if (recovery !== undefined && description !== undefined) {
      setRecoveryState({
        guard: recovery,
        description,
        canSynchronize: recovery.canSynchronize(),
      });
      return;
    }
    finish();
  }, [finish, isPending, principalId]);

  const cancel = useCallback(() => {
    if (recoveryState?.action) return;
    setRecoveryState(undefined);
    setIsPending(false);
  }, [recoveryState?.action]);

  const synchronize = useCallback(async () => {
    if (!recoveryState || recoveryState.action) return;
    setRecoveryState({
      ...recoveryState,
      action: "synchronize",
      error: undefined,
    });
    try {
      if (await recoveryState.guard.synchronize()) {
        finish();
        return;
      }
      setRecoveryState({
        ...recoveryState,
        error:
          "Some local Label work could not synchronize. It has not been discarded.",
      });
    } catch {
      setRecoveryState({
        ...recoveryState,
        error:
          "Some local Label work could not synchronize. It has not been discarded.",
      });
    }
  }, [finish, recoveryState]);

  const download = useCallback(() => {
    if (!recoveryState || recoveryState.action) return;
    setRecoveryState({
      ...recoveryState,
      action: "download",
      error: undefined,
    });
    try {
      recoveryState.guard.download();
      finish();
    } catch {
      setRecoveryState({
        ...recoveryState,
        error: "The recovery bundle could not be downloaded. Try again.",
      });
    }
  }, [finish, recoveryState]);

  const discard = useCallback(() => {
    if (!recoveryState || recoveryState.action) return;
    setRecoveryState({ ...recoveryState, action: "discard", error: undefined });
    finish();
  }, [finish, recoveryState]);

  const recovery = recoveryState
    ? {
        description: recoveryState.description,
        canSynchronize: recoveryState.canSynchronize,
        error: recoveryState.error,
        action: recoveryState.action,
      }
    : undefined;
  return {
    isPending,
    recovery,
    mutate,
    cancel,
    synchronize,
    download,
    discard,
  };
}
